'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import AuditHeader from './components/AuditHeader';
import AuditFilters from './components/AuditFilters';
import AuditTable from './components/AuditTable';
import AuditDetailModal from './components/AuditDetailModal';
import { auditService, type AuditLog, type AuditFilters as AuditFiltersType } from '@/lib/services/staffingService';

export default function AuditPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [filters, setFilters] = useState<AuditFiltersType>({});
  const [totalCount, setTotalCount] = useState(0);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, count } = await auditService.getAll(filters);
      setLogs(data);
      setTotalCount(count);
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleExport = () => {
    const csv = [
      ['Timestamp', 'Performed By', 'Role', 'Action', 'Severity', 'Target', 'Description'].join(','),
      ...logs.map((log) =>
        [
          log.createdAt,
          log.performedByName,
          log.performedByRole,
          log.actionType,
          log.severity,
          log.targetEntity,
          `"${log.description.replace(/"/g, '""')}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full">
        <AuditHeader totalCount={totalCount} onExport={handleExport} />
        <div className="flex-1 overflow-auto px-6 pb-6 space-y-4">
          <AuditFilters filters={filters} onChange={setFilters} />
          {error && (
            <div className="bg-danger/10 border border-danger/20 rounded-lg px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}
          <AuditTable
            logs={logs}
            loading={loading}
            onRowClick={setSelectedLog}
          />
        </div>
      </div>
      {selectedLog && (
        <AuditDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </AppLayout>
  );
}
